import React, { useState } from 'react';
import './App.css';
import { useKeycloak } from './hooks/useKeycloak';
import { useWebSocket } from './hooks/useWebSocket';
import { useApi } from './hooks/useApi';
import { useMaintenanceMode } from './hooks/useMaintenanceMode';
import LoadingSpinner from './components/UI/LoadingSpinner';
import UserInfo from './components/Auth/UserInfo';
import ApiTestSection from './components/WebSocket/ApiTestSection';
import WebSocketClient from './components/WebSocket/WebSocketClient';
import WebSocketManagement from './components/WebSocket/WebSocketManagement';
import BroadcastSection from './components/WebSocket/BroadcastSection';
import Footer from './components/UI/Footer';
import MaintenancePage from './components/System/MaintenancePage';
import BuildInfo from './components/System/BuildInfo';

const App: React.FC = () => {
  const { keycloak, authenticated, loading, userInfo, logout } = useKeycloak();
  const { connected, messages, connect, disconnect } = useWebSocket();
  const { isMaintenanceMode } = useMaintenanceMode();
  const { 
    apiResponse, 
    apiLoading, 
    wsConnections, 
    wsLoading, 
    callApi, 
    fetchConnections, 
    sendBroadcast 
  } = useApi(keycloak);

  const [broadcastMessage, setBroadcastMessage] = useState<string>('');

  const handleLogout = () => {
    disconnect();
    logout();
  };

  const handleSendBroadcast = async () => {
    const success = await sendBroadcast(broadcastMessage);
    if (success) {
      setBroadcastMessage('');
    }
  };

  const getTokenExpiry = (): string => {
    if (!keycloak?.tokenParsed?.exp) return 'N/A';
    return new Date(keycloak.tokenParsed.exp * 1000).toLocaleTimeString();
  };

  // Show maintenance page if backend is in maintenance mode
  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  // Show loading spinner during authentication
  if (loading) {
    return <LoadingSpinner />;
  }

  // If not authenticated, show message (Keycloak will redirect automatically)
  if (!authenticated) {
    return (
      <div className="App">
        <div className="container">
          <h1>🚀 DotnetHub</h1>
          <p>Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Main application for authenticated users
  return (
    <div className="App">
      {authenticated && userInfo && (
        <div className="container">
          <h1>🚀 DotnetHub</h1>
          <p>WebSocket Management Dashboard</p>
          
          <UserInfo userInfo={userInfo} />

          <BuildInfo />

          <ApiTestSection 
            onTestApi={callApi}
            loading={apiLoading}
            response={apiResponse}
          />

          <WebSocketClient
            connected={connected}
            messages={messages}
            onConnect={connect}
            onDisconnect={disconnect}
            keycloak={keycloak}
          />

          <WebSocketManagement
            connections={wsConnections}
            loading={wsLoading}
            onRefresh={fetchConnections}
          />

          <BroadcastSection
            message={broadcastMessage}
            onChange={setBroadcastMessage}
            onSend={handleSendBroadcast}
            disabled={!broadcastMessage.trim()}
          />
          
          <div>
            <button className="btn btn-logout" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
          
          <Footer 
            tokenValid={!!keycloak?.token}
            tokenExpiry={getTokenExpiry()}
          />
        </div>
      )}
    </div>
  );
};

export default App;
